import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  api,
  AdminTrainingReviewSummary,
  AgentCeaSearchResult,
  RegisteredEventMemberSummary,
  TopViewingSitePlan,
  TrainingECBInviteSummary,
  TrainingReviewEventSummary,
  UpcomingEventSummary,
} from '../../services/api';
import {
  formatQrRefreshIntervalInput,
  parseQrRefreshIntervalInput,
} from '../../utils/qrRefreshInterval';
import EventAttendanceQrDialog from './EventAttendanceQrDialog';
import SessionsPanel from './SessionsPanel';
import SessionAttendeesDialog from './SessionAttendeesDialog';
import ManageTransformationEventsTab from './ManageTransformationEventsTab';
import FeatureAccessTab from './FeatureAccessTab';
import type { TrainingSessionSummary } from '../../services/api';
import { DateRangeSelector, DateRangeState } from './DateRangeSelector';
import { getAttendanceCoordinatesWithRetry } from '../../../common/utils/attendanceGeolocation';

interface ManageEventsDialogProps {
  open: boolean;
  onClose: () => void;
}

function formatCategoryLabel(value: string): string {
  return value;
}

type TrainerOption = {
  ceaNumber: string;
  name: string | null;
  mobile?: string | null;
};

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sValid = !Number.isNaN(s.getTime());
  const eValid = !Number.isNaN(e.getTime());
  if (!sValid && !eValid) return '-';
  const fmt = (d: Date) =>
    d.toLocaleString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  if (sValid && eValid) {
    const sameDay =
      s.getFullYear() === e.getFullYear() &&
      s.getMonth() === e.getMonth() &&
      s.getDate() === e.getDate();
    if (sameDay) {
      const timeFmt = (d: Date) =>
        d.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', hour12: false });
      return `${fmt(s)} — ${timeFmt(e)}`;
    }
    return `${fmt(s)} — ${fmt(e)}`;
  }
  return fmt(sValid ? s : e);
}

function normalizeEventDescriptionHtml(html: string): string {
  return html.replace(
    /(src\s*=\s*["'])\/uploads\/event/gi,
    '$1https://portal.huttonsgroup.com/uploads/event',
  );
}

const RECRUITMENT_CATEGORY = 'Recruitment';

// Tabs are rendered in this order:
//   - `overview`       (Event Overview)
//   - `events`         (Categorize Events)
//   - `transformation` (Manage Events (Ad-hoc))
//   - `reviews`        (Vet Training Reviews)
//   - `featureAccess`  (Feature Access)
type ManageEventsMainTab = 'overview' | 'events' | 'transformation' | 'reviews' | 'featureAccess';

const ManageEventsDialog: React.FC<ManageEventsDialogProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<UpcomingEventSummary[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [query, setQuery] = useState<string>('');
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [focusedEventId, setFocusedEventId] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [categoriesDirtied, setCategoriesDirtied] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [projects, setProjects] = useState<TopViewingSitePlan[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [projectsDirtied, setProjectsDirtied] = useState<boolean>(false);
  const [selectedTrainers, setSelectedTrainers] = useState<TrainerOption[]>([]);
  const [trainersDirtied, setTrainersDirtied] = useState<boolean>(false);
  const [locationRequired, setLocationRequired] = useState<boolean>(false);
  const [locationRequiredDirtied, setLocationRequiredDirtied] = useState<boolean>(false);
  const [qrRefreshIntervalSeconds, setQrRefreshIntervalSeconds] = useState<number>(0);
  const [qrRefreshIntervalInput, setQrRefreshIntervalInput] = useState<string>('0');
  const [qrRefreshIntervalDirtied, setQrRefreshIntervalDirtied] = useState<boolean>(false);
  const [trainerSearchInput, setTrainerSearchInput] = useState<string>('');
  const [trainerSearchOptions, setTrainerSearchOptions] = useState<AgentCeaSearchResult[]>([]);
  const [trainerSearchLoading, setTrainerSearchLoading] = useState<boolean>(false);
  const [registeredMembers, setRegisteredMembers] = useState<RegisteredEventMemberSummary[]>([]);
  const [registeredMembersLoading, setRegisteredMembersLoading] = useState<boolean>(false);
  const [registeredMembersError, setRegisteredMembersError] = useState<string | null>(null);
  const [ecbInvites, setEcbInvites] = useState<TrainingECBInviteSummary[]>([]);
  const [ecbInvitesLoading, setEcbInvitesLoading] = useState<boolean>(false);
  const [ecbInvitesError, setEcbInvitesError] = useState<string | null>(null);
  const [eventDetailTab, setEventDetailTab] = useState<'registered' | 'ecbs'>('registered');
  const [registeredPage, setRegisteredPage] = useState<number>(0);
  const [ecbsPage, setEcbsPage] = useState<number>(0);
  const [registeredExportAnchor, setRegisteredExportAnchor] = useState<null | HTMLElement>(null);
  const [ecbExportAnchor, setEcbExportAnchor] = useState<null | HTMLElement>(null);
  const [memberEcbDialogOpen, setMemberEcbDialogOpen] = useState<boolean>(false);
  const [memberEcbDialogTitle, setMemberEcbDialogTitle] = useState<string>('');
  const [memberEcbInvites, setMemberEcbInvites] = useState<TrainingECBInviteSummary[]>([]);
  const [memberEcbInvitesLoading, setMemberEcbInvitesLoading] = useState<boolean>(false);
  const [memberEcbInvitesError, setMemberEcbInvitesError] = useState<string | null>(null);
  const [tagRemovalWarningEventNames, setTagRemovalWarningEventNames] = useState<string[]>([]);
  const [mainTab, setMainTab] = useState<ManageEventsMainTab>('overview');
  // Event Overview tab state — single-select drill-down. The event list
  // here is fetched independently from the Categorize Events list because
  // its scope is broader (past + upcoming, controlled by the checkboxes).
  const [overviewIncludePast, setOverviewIncludePast] = useState<boolean>(false);
  const [overviewIncludeUpcoming, setOverviewIncludeUpcoming] = useState<boolean>(true);
  const [overviewIncludeHip, setOverviewIncludeHip] = useState<boolean>(true);
  const [overviewIncludeTrans, setOverviewIncludeTrans] = useState<boolean>(true);
  const [overviewEvents, setOverviewEvents] = useState<UpcomingEventSummary[]>([]);
  const [overviewLoading, setOverviewLoading] = useState<boolean>(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [overviewQuery, setOverviewQuery] = useState<string>('');
  const [overviewFocusedEventId, setOverviewFocusedEventId] = useState<string | null>(null);
  // Trainings Overview client-side filters. Empty set = no filter on that
  // facet. Multi-select; an event matches if it shares ANY value with each
  // active facet (so picking two categories shows events tagged with either).
  const [overviewCategoryFilter, setOverviewCategoryFilter] = useState<Set<string>>(new Set());
  const [overviewProjectFilter, setOverviewProjectFilter] = useState<Set<string>>(new Set());
  // Optional date-range filter (yyyy-mm-dd, native <input type="date">).
  // When either side is set the Past/Upcoming checkboxes are visually
  // cleared and disabled; the fetch then asks the backend for everything
  // and we filter client-side so the user can pin any window — including
  // ranges that span past + future.
  const [overviewStartDate, setOverviewStartDate] = useState<string>('');
  const [overviewEndDate, setOverviewEndDate] = useState<string>('');
  // Categorize Events lists HIP portal events only. Ad-hoc events are
  // categorized in Manage Events (Ad-hoc).
  const [reviewEvents, setReviewEvents] = useState<TrainingReviewEventSummary[]>([]);
  const [reviewEventsLoading, setReviewEventsLoading] = useState<boolean>(false);
  const [reviewEventsError, setReviewEventsError] = useState<string | null>(null);
  const [selectedReviewEventId, setSelectedReviewEventId] = useState<string>('');
  const [trainingReviews, setTrainingReviews] = useState<AdminTrainingReviewSummary[]>([]);
  const [trainingReviewsLoading, setTrainingReviewsLoading] = useState<boolean>(false);
  const [trainingReviewsError, setTrainingReviewsError] = useState<string | null>(null);
  const [selectedReviewIds, setSelectedReviewIds] = useState<Set<number>>(new Set());
  const [approvingReviews, setApprovingReviews] = useState<boolean>(false);
  const [reviewApprovalSuccess, setReviewApprovalSuccess] = useState<string | null>(null);
  const [reviewDetail, setReviewDetail] = useState<AdminTrainingReviewSummary | null>(null);
  // Session-scoped attendance QR target (from the Sessions section on the
  // Event Overview tab). When set, we render `EventAttendanceQrDialog` with
  // `sessionId` bound so it polls the per-session token endpoint.
  const [sessionQrTarget, setSessionQrTarget] = useState<{
    session: TrainingSessionSummary;
    mode: 'clockIn' | 'clockOut';
    eventName: string;
  } | null>(null);
  // Per-session attendee list dialog.
  const [sessionAttendeesTarget, setSessionAttendeesTarget] =
    useState<TrainingSessionSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Run the three loads independently so a single failure doesn't take
      // down the whole dialog. The events + categories calls are critical;
      // projects is informational and we surface its error separately.
      const [eventsResult, categoriesResult, projectsResult] = await Promise.allSettled([
        api.getUpcomingEvents({
          includeHip: true,
          includeTrans: false,
        }),
        api.getEventCategories(),
        api.getTopViewingSitePlans(),
      ]);

      if (eventsResult.status === 'fulfilled') {
        const normalized = eventsResult.value.map((e) => ({
          ...e,
          assignedCategories: (e.assignedCategories || []).map((c) => c.toUpperCase()),
        }));
        setEvents(normalized);
      } else {
        throw eventsResult.reason instanceof Error
          ? eventsResult.reason
          : new Error('Failed to load events');
      }
      if (categoriesResult.status === 'fulfilled') {
        setCategories(categoriesResult.value.map((c) => c.toUpperCase()));
      } else {
        throw categoriesResult.reason instanceof Error
          ? categoriesResult.reason
          : new Error('Failed to load categories');
      }
      if (projectsResult.status === 'fulfilled') {
        setProjects(projectsResult.value);
      } else {
        // Non-fatal: log and surface a softer warning so admins can still
        // edit categories even if the project dropdown is unavailable.
        console.error('Failed to load projects for categorize dialog:', projectsResult.reason);
        setProjects([]);
        setError(
          projectsResult.reason instanceof Error
            ? `Projects could not be loaded: ${projectsResult.reason.message}`
            : 'Projects could not be loaded.',
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedEventIds(new Set());
      setFocusedEventId(null);
      setSelectedCategories(new Set());
      setCategoriesDirtied(false);
      setSelectedProjectIds(new Set());
      setProjectsDirtied(false);
      setSelectedTrainers([]);
      setTrainersDirtied(false);
      setTrainerSearchInput('');
      setTrainerSearchOptions([]);
      setTrainerSearchLoading(false);
      setRegisteredMembers([]);
      setRegisteredMembersLoading(false);
      setRegisteredMembersError(null);
      setEcbInvites([]);
      setEcbInvitesLoading(false);
      setEcbInvitesError(null);
      setEventDetailTab('registered');
      setRegisteredPage(0);
      setEcbsPage(0);
      setMemberEcbDialogOpen(false);
      setMemberEcbDialogTitle('');
      setMemberEcbInvites([]);
      setMemberEcbInvitesLoading(false);
      setMemberEcbInvitesError(null);
      setTagRemovalWarningEventNames([]);
      setMainTab('overview');
      setOverviewIncludePast(false);
      setOverviewIncludeUpcoming(true);
      setOverviewIncludeHip(true);
      setOverviewIncludeTrans(true);
      setOverviewEvents([]);
      setOverviewLoading(false);
      setOverviewError(null);
      setOverviewQuery('');
      setOverviewFocusedEventId(null);
      setOverviewCategoryFilter(new Set());
      setOverviewProjectFilter(new Set());
      setOverviewStartDate('');
      setOverviewEndDate('');
      setReviewEvents([]);
      setReviewEventsLoading(false);
      setReviewEventsError(null);
      setSelectedReviewEventId('');
      setTrainingReviews([]);
      setTrainingReviewsLoading(false);
      setTrainingReviewsError(null);
      setSelectedReviewIds(new Set());
      setApprovingReviews(false);
      setReviewApprovalSuccess(null);
      setReviewDetail(null);
      setError(null);
      return;
    }
    void load();
  }, [open, load]);

  const selectedEvents = useMemo(
    () => events.filter((e) => selectedEventIds.has(e.id)),
    [events, selectedEventIds],
  );

  /**
   * Prefill categories from the selected event (single-select).
   */
  const commonCategories = useMemo(() => {
    if (selectedEvents.length === 0) return new Set<string>();
    return new Set<string>(selectedEvents[0].assignedCategories);
  }, [selectedEvents]);

  /**
   * Prefill project assignments from the selected event.
   */
  const commonProjects = useMemo(() => {
    if (selectedEvents.length === 0) return new Set<string>();
    return new Set<string>(
      (selectedEvents[0].assignedProjects || []).map((p) => p.projectId),
    );
  }, [selectedEvents]);

  const commonTrainers = useMemo((): TrainerOption[] => {
    if (selectedEvents.length === 0) return [];
    return [...(selectedEvents[0].assignedTrainers || [])];
  }, [selectedEvents]);

  // When the selection changes, reset category / project / trainer / location
  // pickers to the selected event's values (or CPD-based location defaults).
  useEffect(() => {
    setCategoriesDirtied(false);
    const newSelectedCategories = new Set(commonCategories);

    // Auto-include CPD / GENERIC CPD markers when the HIP category id requires it.
    const hasC17041 = selectedEvents.some((e) => e.categoryId === 'C17041');
    const hasC17042 = selectedEvents.some((e) => e.categoryId === 'C17042');

    if (hasC17041) {
      newSelectedCategories.add('CPD');
    }
    if (hasC17042) {
      newSelectedCategories.add('GENERIC CPD');
    }

    setSelectedCategories(newSelectedCategories);
    setProjectsDirtied(false);
    setSelectedProjectIds(new Set(commonProjects));
    setTrainersDirtied(false);
    setSelectedTrainers([...commonTrainers]);
    setTrainerSearchInput('');
    setLocationRequiredDirtied(false);
    const locationValues = selectedEvents.map((e) => e.locationRequired === true);
    const allTrue = selectedEvents.length > 0 && locationValues.every(Boolean);
    const allFalseOrNull =
      selectedEvents.length > 0 &&
      selectedEvents.every((e) => e.locationRequired !== true);
    const anyExplicit =
      selectedEvents.some((e) => e.locationRequired === true || e.locationRequired === false);
    if (allTrue) {
      setLocationRequired(true);
    } else if (allFalseOrNull && anyExplicit) {
      setLocationRequired(false);
    } else {
      // Unsaved / mixed / all-null → default checked for CPD / GENERIC CPD.
      setLocationRequired(hasC17041 || hasC17042);
    }
    setQrRefreshIntervalDirtied(false);
    const storedQr = selectedEvents[0]?.qrRefreshIntervalSeconds ?? 0;
    // CPD / GENERIC CPD default to 30s when stored value is 0.
    const nextQr =
      storedQr > 0 ? storedQr : (hasC17041 || hasC17042 ? 30 : 0);
    setQrRefreshIntervalSeconds(nextQr);
    setQrRefreshIntervalInput(formatQrRefreshIntervalInput(nextQr));
    // We deliberately only re-run this when the selection identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventIds, selectedEvents, commonCategories, commonProjects, commonTrainers]);

  useEffect(() => {
    if (!open || mainTab !== 'events') {
      setTrainerSearchOptions([]);
      setTrainerSearchLoading(false);
      return;
    }
    const q = trainerSearchInput.trim();
    if (q.length <= 2) {
      setTrainerSearchOptions([]);
      setTrainerSearchLoading(false);
      return;
    }
    let cancelled = false;
    setTrainerSearchLoading(true);
    const timer = window.setTimeout(() => {
      void api
        .searchAgentsByCea(q)
        .then((agents) => {
          if (!cancelled) setTrainerSearchOptions(agents);
        })
        .catch(() => {
          if (!cancelled) setTrainerSearchOptions([]);
        })
        .finally(() => {
          if (!cancelled) setTrainerSearchLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, mainTab, trainerSearchInput]);

  const displayedCategories = useMemo(() => {
    // Filter out CPD and GENERIC CPD from standard categories by default
    const list = categories.filter(
      (c) => c.toUpperCase() !== 'CPD' && c.toUpperCase() !== 'GENERIC CPD'
    );
    const hasC17041 = selectedEvents.some((e) => e.categoryId === 'C17041');
    const hasC17042 = selectedEvents.some((e) => e.categoryId === 'C17042');

    if (hasC17041) {
      list.push('CPD');
    }
    if (hasC17042) {
      list.push('GENERIC CPD');
    }
    return list;
  }, [categories, selectedEvents]);

  // Quick lookup so the event list can render `Project Name` chips instead of
  // raw IDs. Falls back to the ID if the project list hasn't loaded yet or the
  // ID isn't in the visible-projects set.
  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects) {
      if (p.projectId) map.set(p.projectId, p.projectName || p.projectId);
    }
    return map;
  }, [projects]);

  const filteredEvents = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => {
      const haystack = `${e.name} ${e.venue || ''} ${e.description || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [events, query]);

  const groupedEvents = useMemo(() => {
    const groups = new Map<string, { label: string; events: UpcomingEventSummary[] }>();
    for (const event of filteredEvents) {
      const d = new Date(event.startDate);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('en-SG', { month: 'long', year: 'numeric' });
      const entry = groups.get(key);
      if (entry) {
        entry.events.push(event);
      } else {
        groups.set(key, { label, events: [event] });
      }
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => ({ key, ...value }));
  }, [filteredEvents]);

  const toggleEvent = useCallback((eventId: string) => {
    // Single-select only: clicking an event focuses it (or clears if already selected).
    setSelectedEventIds((prev) => {
      if (prev.has(eventId) && prev.size === 1) {
        setFocusedEventId(null);
        return new Set();
      }
      setFocusedEventId(eventId);
      return new Set([eventId]);
    });
  }, []);

  const focusedEvent = useMemo(
    () => events.find((e) => e.id === focusedEventId) || null,
    [events, focusedEventId],
  );

  // The "right panel" for the Event Overview tab — single-select, drawn
  // from the overview events list (which can include past events).
  const overviewFocusedEvent = useMemo(
    () => overviewEvents.find((e) => e.id === overviewFocusedEventId) || null,
    [overviewEvents, overviewFocusedEventId],
  );

  const ROWS_PER_PAGE = 25;

  // Reset page numbers when focused event changes
  useEffect(() => {
    setRegisteredPage(0);
    setEcbsPage(0);
  }, [overviewFocusedEventId]);

  const paginatedRegisteredMembers = useMemo(() => {
    return registeredMembers.slice(registeredPage * ROWS_PER_PAGE, (registeredPage + 1) * ROWS_PER_PAGE);
  }, [registeredMembers, registeredPage]);

  const paginatedEcbInvites = useMemo(() => {
    return ecbInvites.slice(ecbsPage * ROWS_PER_PAGE, (ecbsPage + 1) * ROWS_PER_PAGE);
  }, [ecbInvites, ecbsPage]);

  const downloadTableData = (type: 'csv' | 'excel', filename: string, headers: string[], rows: string[][]) => {
    let blob: Blob;
    let finalFilename = filename;

    if (type === 'csv') {
      const escapedRows = rows.map((row) =>
        row.map((val) => {
          const str = String(val).replace(/"/g, '""');
          return `"${str}"`;
        }).join(',')
      );
      const csvContent = '\uFEFF' + [headers.map(h => `"${h.replace(/"/g, '""')}"`).join(','), ...escapedRows].join('\n');
      blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      finalFilename += '.csv';
    } else {
      const escapedRows = rows.map((row) =>
        row.map((val) => {
          return String(val)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
        })
      );
      const escapedHeaders = headers.map((h) =>
        h.replace(/&/g, '&amp;')
         .replace(/</g, '&lt;')
         .replace(/>/g, '&gt;')
         .replace(/"/g, '&quot;')
         .replace(/'/g, '&#039;')
      );

      const htmlContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="content-type" content="text/plain; charset=UTF-8"/>
          <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>Sheet1</x:Name>
                  <x:WorksheetOptions>
                    <x:DisplayGridlines/>
                  </x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
          <![endif]-->
        </head>
        <body>
          <table border="1">
            <thead>
              <tr style="background-color: #f2f2f2; font-weight: bold;">
                ${escapedHeaders.map(h => `<th>${h}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${escapedRows.map(row => `<tr>${row.map(val => `<td>${val}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;
      blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      finalFilename += '.xls';
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', finalFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportRegistered = useCallback((format: 'csv' | 'excel') => {
    if (!overviewFocusedEvent || registeredMembers.length === 0) return;

    const headers = [
      'Name',
      'CEA Number',
      'Phone Number',
      'Registered from Transformation app',
      'ECB Invites Count',
    ];

    const rows = registeredMembers.map((member) => [
      member.name || '-',
      member.ceaNumber || '-',
      member.mobile || '-',
      member.registeredFromTransformationApp ? 'Yes' : 'No',
      String(member.ecbCount || 0),
    ]);

    const eventNameSafe = overviewFocusedEvent.name
      ? overviewFocusedEvent.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      : 'event';

    downloadTableData(format, `${eventNameSafe}_registered_users`, headers, rows);
  }, [overviewFocusedEvent, registeredMembers]);

  const handleExportEcbs = useCallback((format: 'csv' | 'excel') => {
    if (!overviewFocusedEvent || ecbInvites.length === 0) return;

    const headers = ['Name', 'Agency', 'Phone Number', 'Invited By'];

    const rows = ecbInvites.map((ecb) => [
      ecb.name || '-',
      ecb.agency || '-',
      ecb.mobile || '-',
      ecb.invitedByName || ecb.invitedBy || '-',
    ]);

    const eventNameSafe = overviewFocusedEvent.name
      ? overviewFocusedEvent.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      : 'event';

    downloadTableData(format, `${eventNameSafe}_ecb_invites`, headers, rows);
  }, [overviewFocusedEvent, ecbInvites]);

  // Events flagged with `Event.RegistrationRequired = 0` don't have bookings,
  // so the Registered Users / ECB tabs have no data to show. Treat null /
  // undefined as "registration required" for back-compat with legacy rows.
  const overviewFocusedEventSkipsRegistration =
    overviewFocusedEvent?.registrationRequired === false;

  // When the focused event flips to a no-registration event while the
  // (now-hidden) Registered Users tab is active, fall back to Attended
  // Users. Don't touch the ECB tab — it stays selectable for these
  // events.
  useEffect(() => {
    if (overviewFocusedEventSkipsRegistration && eventDetailTab === 'registered') {
      setEventDetailTab('ecbs');
    }
  }, [overviewFocusedEventSkipsRegistration, eventDetailTab]);


  // Fetches the three per-event lists (registered, ECBs, attended) for the
  // currently focused event. Extracted from the load effect so the refresh
  // button can re-trigger it on demand without re-mounting the panel.
  const loadFocusedEventMembers = useCallback((eventId: string) => {
    setRegisteredMembersLoading(true);
    setEcbInvitesLoading(true);
    setRegisteredMembersError(null);
    setEcbInvitesError(null);

    api.getRegisteredEventMembers(eventId)
      .then((members) => setRegisteredMembers(members))
      .catch((e) => {
        setRegisteredMembers([]);
        setRegisteredMembersError(
          e instanceof Error ? e.message : 'Failed to load registered members',
        );
      })
      .finally(() => setRegisteredMembersLoading(false));

    api.getEventECBInvites(eventId)
      .then((ecbs) => setEcbInvites(ecbs))
      .catch((e) => {
        setEcbInvites([]);
        setEcbInvitesError(e instanceof Error ? e.message : 'Failed to load ECBs');
      })
      .finally(() => setEcbInvitesLoading(false));

  }, []);

  // Member tabs (Registered / Attended / ECBs) live exclusively on the
  // Event Overview tab now, so we key the fetch off
  // `overviewFocusedEventId` rather than the Categorize Events
  // single-select state. Both tabs are mutually exclusive in the UI, so
  // there's never a conflict.
  useEffect(() => {
    if (!open || !overviewFocusedEventId) {
      setRegisteredMembers([]);
      setRegisteredMembersLoading(false);
      setRegisteredMembersError(null);
      setEcbInvites([]);
      setEcbInvitesLoading(false);
      setEcbInvitesError(null);
      return;
    }
    if (overviewFocusedEventId.startsWith('te:')) {
      setRegisteredMembers([]);
      setRegisteredMembersLoading(false);
      setRegisteredMembersError(null);
      setEcbInvites([]);
      setEcbInvitesLoading(false);
      setEcbInvitesError(null);
      return;
    }
    loadFocusedEventMembers(overviewFocusedEventId);
  }, [open, overviewFocusedEventId, loadFocusedEventMembers]);

  const overviewDateRangeActive = !!overviewStartDate || !!overviewEndDate;

  // Keep the Past/Upcoming checkboxes in sync with the date range:
  //   • range becomes active  → uncheck both (date range supersedes them)
  //   • range cleared          → restore the default (Upcoming on, Past off)
  // The effect intentionally watches only the active flag, so toggling
  // start/end dates within an already-active range doesn't stomp the
  // checkboxes (they're already disabled anyway).
  useEffect(() => {
    if (overviewDateRangeActive) {
      setOverviewIncludePast(false);
      setOverviewIncludeUpcoming(false);
    } else {
      setOverviewIncludePast(false);
      setOverviewIncludeUpcoming(true);
    }
  }, [overviewDateRangeActive]);

  // Fetch the Event Overview list using the current Past / Upcoming
  // checkboxes. Re-runs when either toggles or when the dialog opens onto
  // (or switches to) the Overview tab. While a date range is active we
  // override the flags to fetch everything categorized so the client-side
  // date filter has the full set to work with.
  const loadOverviewEvents = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const list = await api.getUpcomingEvents({
        includePast: overviewDateRangeActive ? true : overviewIncludePast,
        includeUpcoming: overviewDateRangeActive ? true : overviewIncludeUpcoming,
        // Event Overview is sourced from the TransformationTraining table,
        // so only events with at least one active categorization show up.
        onlyCategorized: true,
        includeHip: overviewIncludeHip,
        includeTrans: overviewIncludeTrans,
      });
      const normalizedList = list.map((e) => ({
        ...e,
        assignedCategories: (e.assignedCategories || []).map((c) => c.toUpperCase()),
      }));
      setOverviewEvents(normalizedList);
      // If the previously-focused event isn't in the new list (e.g. user
      // unticked Upcoming and the row dropped out), clear the focus so
      // the right panel goes back to its empty state.
      setOverviewFocusedEventId((prev) =>
        prev && list.some((e) => e.id === prev) ? prev : null,
      );
    } catch (e) {
      setOverviewEvents([]);
      setOverviewError(e instanceof Error ? e.message : 'Failed to load events');
    } finally {
      setOverviewLoading(false);
    }
  }, [
    overviewIncludePast,
    overviewIncludeUpcoming,
    overviewDateRangeActive,
    overviewIncludeHip,
    overviewIncludeTrans,
  ]);

  useEffect(() => {
    if (!open || mainTab !== 'overview') return;
    void loadOverviewEvents();
  }, [open, mainTab, loadOverviewEvents]);

  const filteredOverviewEvents = useMemo(() => {
    const q = overviewQuery.trim().toLowerCase();
    const hasCategoryFilter = overviewCategoryFilter.size > 0;
    const hasProjectFilter = overviewProjectFilter.size > 0;
    // Inclusive bounds: start at 00:00 of the picked day, end at 23:59:59.999.
    // An event passes if its [startDate, endDate] interval overlaps the
    // selected window in any way (so a multi-day event still shows even
    // when only one of its days falls inside the range).
    const rangeStartMs = overviewStartDate
      ? new Date(`${overviewStartDate}T00:00:00`).getTime()
      : Number.NEGATIVE_INFINITY;
    const rangeEndMs = overviewEndDate
      ? new Date(`${overviewEndDate}T23:59:59.999`).getTime()
      : Number.POSITIVE_INFINITY;
    const hasDateFilter = overviewDateRangeActive;
    if (!q && !hasCategoryFilter && !hasProjectFilter && !hasDateFilter) return overviewEvents;
    return overviewEvents.filter((e) => {
      if (q) {
        const haystack = `${e.name} ${e.venue || ''} ${e.description || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (hasCategoryFilter) {
        const matches = (e.assignedCategories || []).some((c) => overviewCategoryFilter.has(c));
        if (!matches) return false;
      }
      if (hasProjectFilter) {
        const matches = (e.assignedProjects || []).some((p) => overviewProjectFilter.has(p.projectId));
        if (!matches) return false;
      }
      if (hasDateFilter) {
        const evStart = new Date(e.startDate).getTime();
        const evEnd = new Date(e.endDate).getTime();
        if (Number.isNaN(evStart) || Number.isNaN(evEnd)) return false;
        // No overlap with the selected window → exclude.
        if (evEnd < rangeStartMs || evStart > rangeEndMs) return false;
      }
      return true;
    });
  }, [
    overviewEvents,
    overviewQuery,
    overviewCategoryFilter,
    overviewProjectFilter,
    overviewStartDate,
    overviewEndDate,
    overviewDateRangeActive,
  ]);

  const groupedOverviewEvents = useMemo(() => {
    const groups = new Map<string, { label: string; events: UpcomingEventSummary[] }>();
    for (const event of filteredOverviewEvents) {
      const d = new Date(event.startDate);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('en-SG', { month: 'long', year: 'numeric' });
      const entry = groups.get(key);
      if (entry) entry.events.push(event);
      else groups.set(key, { label, events: [event] });
    }
    // Newest month first when past events are visible; otherwise chronological.
    const sortDir = overviewIncludePast ? -1 : 1;
    return Array.from(groups.entries())
      .sort(([a], [b]) => sortDir * a.localeCompare(b))
      .map(([key, value]) => ({ key, ...value }));
  }, [filteredOverviewEvents, overviewIncludePast]);

  const loadReviewEvents = useCallback(async () => {
    setReviewEventsLoading(true);
    setReviewEventsError(null);
    try {
      const list = await api.getTrainingReviewEvents();
      setReviewEvents(list);
      setSelectedReviewEventId((prev) => prev || list[0]?.eventId || '');
    } catch (e) {
      setReviewEvents([]);
      setReviewEventsError(e instanceof Error ? e.message : 'Failed to load review events');
    } finally {
      setReviewEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || mainTab !== 'reviews') return;
    void loadReviewEvents();
  }, [open, mainTab, loadReviewEvents]);

  useEffect(() => {
    let cancelled = false;
    if (!open || mainTab !== 'reviews' || !selectedReviewEventId) {
      setTrainingReviews([]);
      setSelectedReviewIds(new Set());
      setTrainingReviewsLoading(false);
      setTrainingReviewsError(null);
      return;
    }

    setTrainingReviewsLoading(true);
    setTrainingReviewsError(null);
    setSelectedReviewIds(new Set());
    api.getTrainingReviewsForEvent(selectedReviewEventId)
      .then((reviews) => {
        if (!cancelled) setTrainingReviews(reviews);
      })
      .catch((e) => {
        if (!cancelled) {
          setTrainingReviews([]);
          setTrainingReviewsError(e instanceof Error ? e.message : 'Failed to load training reviews');
        }
      })
      .finally(() => {
        if (!cancelled) setTrainingReviewsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, mainTab, selectedReviewEventId]);

  const pendingTrainingReviews = useMemo(
    () => trainingReviews.filter((review) => !review.dateApproved),
    [trainingReviews],
  );

  const toggleReviewSelection = useCallback((reviewId: number) => {
    setReviewApprovalSuccess(null);
    setSelectedReviewIds((prev) => {
      const next = new Set(prev);
      if (next.has(reviewId)) next.delete(reviewId);
      else next.add(reviewId);
      return next;
    });
  }, []);

  const toggleAllPendingReviews = useCallback(() => {
    setReviewApprovalSuccess(null);
    setSelectedReviewIds((prev) => {
      if (pendingTrainingReviews.length > 0 && pendingTrainingReviews.every((review) => prev.has(review.id))) {
        return new Set();
      }
      return new Set(pendingTrainingReviews.map((review) => review.id));
    });
  }, [pendingTrainingReviews]);

  const handleApproveSelectedReviews = useCallback(async () => {
    const ids = Array.from(selectedReviewIds);
    if (ids.length === 0) return;
    setApprovingReviews(true);
    setTrainingReviewsError(null);
    setReviewApprovalSuccess(null);
    try {
      const result = await api.approveTrainingReviews(ids);
      setReviewApprovalSuccess(`Approved ${result.approvedCount} review${result.approvedCount === 1 ? '' : 's'}.`);
      if (selectedReviewEventId) {
        const [reviews, eventsWithReviews] = await Promise.all([
          api.getTrainingReviewsForEvent(selectedReviewEventId),
          api.getTrainingReviewEvents(),
        ]);
        setTrainingReviews(reviews);
        setReviewEvents(eventsWithReviews);
      }
      setSelectedReviewIds(new Set());
    } catch (e) {
      setTrainingReviewsError(e instanceof Error ? e.message : 'Failed to approve reviews');
    } finally {
      setApprovingReviews(false);
    }
  }, [selectedReviewIds, selectedReviewEventId]);

  const handleOpenMemberEcbDialog = useCallback(async (member: RegisteredEventMemberSummary) => {
    // The Registered Users table now lives on the Event Overview tab, so
    // its "View ECBs" button keys off the overview-focused event id.
    if (!overviewFocusedEventId || !member.ceaNumber) return;
    setMemberEcbDialogOpen(true);
    setMemberEcbDialogTitle(`${member.name || member.ceaNumber}'s ECBs`);
    setMemberEcbInvites([]);
    setMemberEcbInvitesError(null);
    setMemberEcbInvitesLoading(true);
    try {
      const ecbs = await api.getEventECBInvitesForInviter(overviewFocusedEventId, member.ceaNumber);
      setMemberEcbInvites(ecbs);
    } catch (e) {
      setMemberEcbInvitesError(e instanceof Error ? e.message : 'Failed to load ECBs');
    } finally {
      setMemberEcbInvitesLoading(false);
    }
  }, [overviewFocusedEventId]);

  const toggleCategory = (value: string) => {
    if (
      value === 'CPD' ||
      value === 'GENERIC CPD' ||
      value.toUpperCase() === 'CPD' ||
      value.toUpperCase() === 'GENERIC CPD'
    ) {
      return;
    }
    setCategoriesDirtied(true);
    setSelectedCategories((prev) => {
      if (value === RECRUITMENT_CATEGORY) {
        setProjectsDirtied(true);
        setSelectedProjectIds(new Set());
        return prev.has(value) ? new Set() : new Set([value]);
      }
      const next = new Set(prev);
      next.delete(RECRUITMENT_CATEGORY);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const recruitmentSelected = selectedCategories.has(RECRUITMENT_CATEGORY);

  const isDirty = useMemo(() => {
    if (selectedEvents.length === 0) return false;
    if (
      categoriesDirtied ||
      projectsDirtied ||
      trainersDirtied ||
      locationRequiredDirtied ||
      qrRefreshIntervalDirtied
    ) {
      return true;
    }
    // Enabled if CPD or GENERIC CPD is auto-selected
    if (selectedCategories.has('CPD') || selectedCategories.has('GENERIC CPD')) {
      return true;
    }
    return false;
  }, [
    selectedEvents.length,
    categoriesDirtied,
    projectsDirtied,
    trainersDirtied,
    locationRequiredDirtied,
    qrRefreshIntervalDirtied,
    selectedCategories,
  ]);

  const getEventsWithRemovedTagsAndMembers = useCallback(async () => {
    const affectedEventNames: string[] = [];
    for (const event of selectedEvents) {
      const removesCategory = (event.assignedCategories || [])
        .some((category) => !selectedCategories.has(category));
      const removesProject = (event.assignedProjects || [])
        .some((project) => !selectedProjectIds.has(project.projectId));
      if (!removesCategory && !removesProject) continue;

      const members = await api.getRegisteredEventMembers(event.id);
      if (members.length > 0) {
        affectedEventNames.push(event.name);
      }
    }
    return affectedEventNames;
  }, [selectedEvents, selectedCategories, selectedProjectIds]);

  const saveCategories = useCallback(async () => {
    if (selectedEvents.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const nextCategories = Array.from(selectedCategories).map((c) => c.toUpperCase());
      const nextProjectIds = Array.from(selectedProjectIds);
      const nextTrainerCeas = selectedTrainers.map((t) => t.ceaNumber);
      const eventIds = selectedEvents.map((e) => e.id);
      await api.bulkCategorizeEvents(
        eventIds,
        nextCategories,
        nextProjectIds,
        nextTrainerCeas,
        locationRequired,
        qrRefreshIntervalSeconds,
      );
      const eventIdSet = new Set(eventIds);
      setEvents((prev) =>
        prev.map((e) =>
          eventIdSet.has(e.id)
            ? {
                ...e,
                assignedCategories: nextCategories,
                assignedProjects: nextProjectIds.map((projectId) => {
                  const option = projects.find((p) => p.projectId === projectId);
                  return {
                    projectId,
                    projectName: option?.projectName ?? projectNameById.get(projectId) ?? null,
                  };
                }),
                assignedTrainers: selectedTrainers.map((trainer) => ({
                  ceaNumber: trainer.ceaNumber,
                  name: trainer.name,
                  mobile: trainer.mobile ?? null,
                })),
                locationRequired,
                qrRefreshIntervalSeconds,
              }
            : e,
        ),
      );
      setCategoriesDirtied(false);
      setProjectsDirtied(false);
      setTrainersDirtied(false);
      setLocationRequiredDirtied(false);
      setQrRefreshIntervalDirtied(false);
      setToast({
        severity: 'success',
        message: `Saved categories for ${eventIds.length} event${eventIds.length === 1 ? '' : 's'}.`,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save';
      setError(message);
      setToast({ severity: 'error', message });
    } finally {
      setSaving(false);
    }
  }, [
    selectedEvents,
    selectedCategories,
    selectedProjectIds,
    selectedTrainers,
    locationRequired,
    qrRefreshIntervalSeconds,
    projects,
    projectNameById,
  ]);

  const handleSave = useCallback(async () => {
    if (selectedEvents.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const affectedEventNames = await getEventsWithRemovedTagsAndMembers();
      if (affectedEventNames.length > 0) {
        setTagRemovalWarningEventNames(affectedEventNames);
        return;
      }
      await saveCategories();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to check registered members';
      setError(message);
      setToast({ severity: 'error', message });
    } finally {
      setSaving(false);
    }
  }, [selectedEvents.length, getEventsWithRemovedTagsAndMembers, saveCategories]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '85vh',
          width: '97vw',
          maxWidth: 1800,
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Manage Events</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Tabs
          value={mainTab}
          onChange={(_, value) => setMainTab(value)}
          // On narrow viewports the three labels are wider than the
          // dialog (the screenshot showed "Categorize E..." clipped and
          // the third tab entirely off-screen), so let the tab strip
          // scroll horizontally with discoverable scroll buttons.
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2, maxWidth: '100%' }}
        >
          <Tab
            value="overview"
            label="Events Overview"
            sx={{ textTransform: 'none', fontWeight: 600, px: { xs: 1.25, sm: 2 } }}
          />
          <Tab
            value="events"
            label="Manage Events (HIP)"
            sx={{ textTransform: 'none', fontWeight: 600, px: { xs: 1.25, sm: 2 } }}
          />
          <Tab
            value="transformation"
            label="Manage Events (Ad-hoc)"
            sx={{ textTransform: 'none', fontWeight: 600, px: { xs: 1.25, sm: 2 } }}
          />
          <Tab
            value="reviews"
            label="Vet Training Reviews"
            sx={{ textTransform: 'none', fontWeight: 600, px: { xs: 1.25, sm: 2 } }}
          />
          <Tab
            value="featureAccess"
            label="Feature Access"
            sx={{ textTransform: 'none', fontWeight: 600, px: { xs: 1.25, sm: 2 } }}
          />
        </Tabs>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {mainTab === 'transformation' ? (
          <ManageTransformationEventsTab
            open={open && mainTab === 'transformation'}
            categories={categories}
            projects={projects}
          />
        ) : mainTab === 'featureAccess' ? (
          <FeatureAccessTab open={open && mainTab === 'featureAccess'} />
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : mainTab === 'reviews' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 2fr) 3px 3fr' },
              gap: 2,
              minHeight: 480,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Events with Reviews
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 560,
                }}
              >
                {reviewEventsError ? (
                  <Alert severity="error" sx={{ m: 1 }}>{reviewEventsError}</Alert>
                ) : reviewEventsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : reviewEvents.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No training reviews found.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {reviewEvents.map((event) => {
                      const selected = event.eventId === selectedReviewEventId;
                      return (
                        <ListItemButton
                          key={event.eventId}
                          selected={selected}
                          onClick={() => {
                            setSelectedReviewEventId(event.eventId);
                            setReviewApprovalSuccess(null);
                          }}
                          sx={{
                            alignItems: 'flex-start',
                            borderLeft: selected ? '3px solid var(--color-accent)' : '3px solid transparent',
                            '&.Mui-selected': {
                              bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                              '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.15)' },
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {event.name}
                              </Typography>
                            }
                            secondary={
                              <Stack spacing={0.25} component="span">
                                <Typography variant="caption" color="text.secondary" component="span">
                                  {formatDateRange(event.startDate, event.endDate)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="span">
                                  {event.reviewCount} review{event.reviewCount === 1 ? '' : 's'} · {event.pendingReviewCount} pending
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Reviews
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedReviewIds.size} selected
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void handleApproveSelectedReviews()}
                  disabled={selectedReviewIds.size === 0 || approvingReviews}
                  sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
                >
                  {approvingReviews ? 'Approving...' : 'Approve selected'}
                </Button>
              </Stack>
              {reviewApprovalSuccess && (
                <Alert severity="success" sx={{ mb: 1 }} onClose={() => setReviewApprovalSuccess(null)}>
                  {reviewApprovalSuccess}
                </Alert>
              )}
              {trainingReviewsError ? (
                <Alert severity="error">{trainingReviewsError}</Alert>
              ) : trainingReviewsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !selectedReviewEventId ? (
                <Box sx={{ p: 3, border: '1px dashed rgba(var(--color-text-rgb), 0.15)', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Select an event to view reviews.
                  </Typography>
                </Box>
              ) : trainingReviews.length === 0 ? (
                <Box sx={{ p: 3, border: '1px dashed rgba(var(--color-text-rgb), 0.15)', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No reviews for this event.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={pendingTrainingReviews.length > 0 && pendingTrainingReviews.every((review) => selectedReviewIds.has(review.id))}
                            indeterminate={selectedReviewIds.size > 0 && !pendingTrainingReviews.every((review) => selectedReviewIds.has(review.id))}
                            onChange={toggleAllPendingReviews}
                            disabled={pendingTrainingReviews.length === 0}
                          />
                        </TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>CEA Number</TableCell>
                        <TableCell>Review</TableCell>
                        <TableCell>Learning Points</TableCell>
                        <TableCell>Approved</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trainingReviews.map((review) => {
                        const approved = Boolean(review.dateApproved);
                        const truncatedCellSx = {
                          maxWidth: 320,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        } as const;
                        return (
                          <TableRow
                            key={review.id}
                            selected={selectedReviewIds.has(review.id)}
                            hover
                            onClick={() => setReviewDetail(review)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={selectedReviewIds.has(review.id)}
                                onChange={() => toggleReviewSelection(review.id)}
                                disabled={approved}
                              />
                            </TableCell>
                            <TableCell>{review.name || '-'}</TableCell>
                            <TableCell>{review.ceaNumber || '-'}</TableCell>
                            <TableCell sx={truncatedCellSx} title={review.review || ''}>
                              {review.review || '-'}
                            </TableCell>
                            <TableCell sx={truncatedCellSx} title={review.learningPoints || ''}>
                              {review.learningPoints || '-'}
                            </TableCell>
                            <TableCell>
                              {approved ? (
                                <Chip size="small" color="success" label="Approved" />
                              ) : (
                                <Chip size="small" variant="outlined" label="Pending" />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>
        ) : mainTab === 'overview' ? (
          <Box
            sx={{
              display: 'grid',
              // Tighter left column on Trainings Overview so the focused
              // event detail panel (registration QR + member tabs) gets
              // more horizontal room.
              gridTemplateColumns: { xs: '1fr', md: 'minmax(360px, 1fr) 3px 2fr' },
              gap: 2,
              minHeight: 600,
            }}
          >
            {/* Left: events list (single-select) with Past / Upcoming
                checkbox filters and a search field. Sourced from
                TransformationTraining (only categorized events) so this
                tab can include past events without affecting the
                Categorize Events list. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Alert
                severity="info"
                icon={false}
                sx={{
                  mb: 1,
                  py: 0,
                  px: 1.25,
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  '& .MuiAlert-message': { py: 0.5 },
                }}
              >
                Only events that have been categorized are shown here.
              </Alert>
              <TextField
                size="small"
                placeholder="Search events..."
                value={overviewQuery}
                onChange={(e) => setOverviewQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              {/* Faceted filters — Category and Project sit side-by-side
                  to keep the filter strip vertically compact. Empty
                  selection = no filter on that facet. */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={categories}
                  value={Array.from(overviewCategoryFilter)}
                  onChange={(_, value) => setOverviewCategoryFilter(new Set(value))}
                  getOptionLabel={(opt) => formatCategoryLabel(opt)}
                  isOptionEqualToValue={(option, value) => option === value}
                  sx={{ flex: 1, minWidth: 0 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                      placeholder={
                        overviewCategoryFilter.size === 0 ? 'Filter by category' : ''
                      }
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, idx) => (
                      <Chip
                        {...getTagProps({ index: idx })}
                        key={opt}
                        label={formatCategoryLabel(opt)}
                        size="small"
                      />
                    ))
                  }
                />
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={projects}
                  value={Array.from(overviewProjectFilter)
                    .map((id) => projects.find((p) => p.projectId === id))
                    .filter((p): p is TopViewingSitePlan => Boolean(p))}
                  onChange={(_, value) =>
                    setOverviewProjectFilter(new Set(value.map((p) => p.projectId)))
                  }
                  getOptionLabel={(opt) => opt.projectName || opt.projectId}
                  isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
                  sx={{ flex: 1, minWidth: 0 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                      placeholder={
                        overviewProjectFilter.size === 0 ? 'Filter by project' : ''
                      }
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, idx) => (
                      <Chip
                        {...getTagProps({ index: idx })}
                        key={opt.projectId}
                        label={opt.projectName || opt.projectId}
                        size="small"
                      />
                    ))
                  }
                />
              </Stack>
              {/* Combined date range + Past/Upcoming + refresh row.
                  The two controls are mutually exclusive (date range
                  disables the checkboxes and vice-versa), so packing
                  them onto one row keeps the filter strip short. */}
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
              >
                <DateRangeSelector
                  label="Date range"
                  buttonMinWidth={240}
                  range={{
                    startDate: overviewStartDate,
                    endDate: overviewEndDate,
                  }}
                  onApply={(next: DateRangeState) => {
                    setOverviewStartDate(next.startDate);
                    setOverviewEndDate(next.endDate);
                  }}
                />
                {overviewDateRangeActive && (
                  <Tooltip title="Clear date range">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setOverviewStartDate('');
                        setOverviewEndDate('');
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    gap: 0.5,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={overviewIncludeUpcoming}
                        onChange={(e) => setOverviewIncludeUpcoming(e.target.checked)}
                        disabled={overviewDateRangeActive}
                      />
                    }
                    label="Upcoming"
                    sx={{ mr: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={overviewIncludePast}
                        onChange={(e) => setOverviewIncludePast(e.target.checked)}
                        disabled={overviewDateRangeActive}
                      />
                    }
                    label="Past"
                    sx={{ mr: 0 }}
                  />
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    gap: 0.5,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={overviewIncludeHip}
                        onChange={(e) => setOverviewIncludeHip(e.target.checked)}
                      />
                    }
                    label="HIP"
                    sx={{ mr: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={overviewIncludeTrans}
                        onChange={(e) => setOverviewIncludeTrans(e.target.checked)}
                      />
                    }
                    label="Ad-hoc"
                    sx={{ mr: 0 }}
                  />
                  <Tooltip title="Refresh">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => void loadOverviewEvents()}
                        disabled={overviewLoading}
                      >
                        {overviewLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Stack>
              {overviewError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setOverviewError(null)}>
                  {overviewError}
                </Alert>
              )}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 'calc(85vh - 360px)',
                }}
              >
                {overviewLoading && overviewEvents.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : !overviewIncludePast && !overviewIncludeUpcoming && !overviewDateRangeActive ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Select at least one of Past or Upcoming to view events.
                    </Typography>
                  </Box>
                ) : !overviewIncludeHip && !overviewIncludeTrans ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Select at least one of HIP or Ad-hoc to view events.
                    </Typography>
                  </Box>
                ) : filteredOverviewEvents.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No events match the current filters.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding subheader={<li />}>
                    {groupedOverviewEvents.map((group) => (
                      <li key={group.key}>
                        <ul style={{ padding: 0, listStyle: 'none' }}>
                          <ListSubheader
                            disableSticky={false}
                            sx={{
                              bgcolor: 'var(--color-surface)',
                              color: 'var(--color-text-strong)',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              lineHeight: '2.25rem',
                              borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
                            }}
                          >
                            {group.label}
                          </ListSubheader>
                          {group.events.map((event) => {
                            const isSelected = overviewFocusedEventId === event.id;
                            return (
                              <ListItemButton
                                key={event.id}
                                selected={isSelected}
                                onClick={() => setOverviewFocusedEventId(event.id)}
                                sx={{
                                  alignItems: 'flex-start',
                                  borderLeft: isSelected
                                    ? '3px solid var(--color-accent)'
                                    : '3px solid transparent',
                                  '&.Mui-selected': {
                                    bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                                    '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.15)' },
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {event.name}
                                      </Typography>
                                      <Chip
                                        label={event.sourceType === 'TRANS' ? 'Ad-hoc' : 'HIP'}
                                        size="small"
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                      />
                                    </Stack>
                                  }
                                  secondary={
                                    <Stack spacing={0.25} component="span">
                                      <Typography variant="caption" color="text.secondary" component="span">
                                        {formatDateRange(event.startDate, event.endDate)}
                                      </Typography>
                                      {event.venue && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          component="span"
                                        >
                                          {event.venue}
                                        </Typography>
                                      )}
                                      {event.assignedCategories.length > 0 && (
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          sx={{ mt: 0.5, flexWrap: 'wrap' }}
                                          component="span"
                                        >
                                          {event.assignedCategories.map((c) => (
                                            <Chip
                                              key={c}
                                              size="small"
                                              label={formatCategoryLabel(c)}
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                          ))}
                                        </Stack>
                                      )}
                                      {event.assignedProjects && event.assignedProjects.length > 0 && (
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          sx={{ mt: 0.5, flexWrap: 'wrap' }}
                                          component="span"
                                        >
                                          {event.assignedProjects.map((project) => (
                                            <Chip
                                              key={project.projectId}
                                              size="small"
                                              variant="outlined"
                                              label={
                                                project.projectName ||
                                                projectNameById.get(project.projectId) ||
                                                project.projectId
                                              }
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                          ))}
                                        </Stack>
                                      )}
                                    </Stack>
                                  }
                                />
                              </ListItemButton>
                            );
                          })}
                        </ul>
                      </li>
                    ))}
                  </List>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {filteredOverviewEvents.length} event{filteredOverviewEvents.length === 1 ? '' : 's'}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            {/* Right: focused event details + Registration QR + member tabs. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {!overviewFocusedEvent ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Select an event on the left to view its details.
                  </Typography>
                </Box>
              ) : (() => {
                // Event-level attendance QR + clock-out QR buttons used
                // to live in the top-right of this panel, gated to the
                // event's [startDate 00:00, endDate 23:59] window. Both
                // buttons were removed — attendance is now issued per
                // session via the Sessions section below, and admins can
                // show a session's QR regardless of wall-clock time.
                const isRefreshing =
                  registeredMembersLoading ||
                  ecbInvitesLoading;
                return (
                  <Stack spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {overviewFocusedEvent.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateRange(overviewFocusedEvent.startDate, overviewFocusedEvent.endDate)}
                      </Typography>
                      {overviewFocusedEvent.venue && (
                        <Typography variant="body2" color="text.secondary">
                          {overviewFocusedEvent.venue}
                        </Typography>
                      )}
                    </Box>

                    {overviewFocusedEvent.assignedCategories.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                        {overviewFocusedEvent.assignedCategories.map((c) => (
                          <Chip
                            key={c}
                            size="small"
                            label={formatCategoryLabel(c)}
                            sx={{ height: 22, fontSize: '0.72rem' }}
                          />
                        ))}
                      </Stack>
                    )}

                    {overviewFocusedEvent.assignedProjects && overviewFocusedEvent.assignedProjects.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                        {overviewFocusedEvent.assignedProjects.map((project) => (
                          <Chip
                            key={project.projectId}
                            size="small"
                            variant="outlined"
                            label={
                              project.projectName ||
                              projectNameById.get(project.projectId) ||
                              project.projectId
                            }
                            sx={{ height: 22, fontSize: '0.72rem' }}
                          />
                        ))}
                      </Stack>
                    )}

                    {overviewFocusedEvent.description && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Description
                        </Typography>
                        <Box
                          sx={{
                            p: 1.5,
                            border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                            borderRadius: 1,
                            bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                            color: 'var(--color-text)',
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                            maxHeight: 220,
                            overflow: 'auto',
                            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                            '& p': { mt: 0, mb: 1 },
                            '& p:last-child': { mb: 0 },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: normalizeEventDescriptionHtml(overviewFocusedEvent.description),
                          }}
                        />
                      </Box>
                    )}

                    {overviewFocusedEvent && (
                      <Box>
                        <SessionsPanel
                          eventId={overviewFocusedEvent.id}
                          eventStartDate={overviewFocusedEvent.startDate}
                          eventEndDate={overviewFocusedEvent.endDate || overviewFocusedEvent.startDate}
                          mode="overview"
                          onShowQr={(session, qrMode) =>
                            setSessionQrTarget({
                              session,
                              mode: qrMode,
                              eventName: overviewFocusedEvent.name,
                            })
                          }
                          onShowAttendees={(session) => setSessionAttendeesTarget(session)}
                        />
                      </Box>
                    )}

                    <Box>
                      {overviewFocusedEventSkipsRegistration && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                          This event does not require registration. Attendees can simply turn up
                          and mark their attendance.
                        </Alert>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                          borderBottom: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Tabs
                          value={eventDetailTab}
                          onChange={(_, value) => setEventDetailTab(value)}
                          // Three count-bearing labels (e.g. "Registered
                          // Users (12)") overflow the right-hand panel
                          // on narrow widths, so the strip scrolls and
                          // the labels render sentence-case for parity
                          // with the outer tab strip.
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                          sx={{ minHeight: 40, flex: 1, minWidth: 0 }}
                        >
                          {!overviewFocusedEventSkipsRegistration && (
                            <Tab
                              value="registered"
                              label={`Registered Users (${registeredMembers.length})`}
                              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, px: { xs: 1, sm: 2 } }}
                            />
                          )}
                          <Tab
                            value="ecbs"
                            label={`ECB (${ecbInvites.length})`}
                            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, px: { xs: 1, sm: 2 } }}
                          />
                        </Tabs>
                        <Tooltip title="Refresh lists" arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (overviewFocusedEventId) {
                                  loadFocusedEventMembers(overviewFocusedEventId);
                                }
                              }}
                              disabled={!overviewFocusedEventId || isRefreshing}
                              aria-label="Refresh event members"
                              sx={{ mr: 0.5 }}
                            >
                              {isRefreshing ? (
                                <CircularProgress size={18} />
                              ) : (
                                <RefreshIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      {!overviewFocusedEventSkipsRegistration && eventDetailTab === 'registered' && (
                        registeredMembersError ? (
                          <Alert severity="error">{registeredMembersError}</Alert>
                        ) : registeredMembersLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : registeredMembers.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No registered users.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                endIcon={<KeyboardArrowDownIcon />}
                                onClick={(e) => setRegisteredExportAnchor(e.currentTarget)}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Export
                              </Button>
                              <Menu
                                anchorEl={registeredExportAnchor}
                                open={Boolean(registeredExportAnchor)}
                                onClose={() => setRegisteredExportAnchor(null)}
                              >
                                <MenuItem onClick={() => { handleExportRegistered('csv'); setRegisteredExportAnchor(null); }}>
                                  Export as CSV
                                </MenuItem>
                                <MenuItem onClick={() => { handleExportRegistered('excel'); setRegisteredExportAnchor(null); }}>
                                  Export as Excel
                                </MenuItem>
                              </Menu>
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>CEA Number</TableCell>
                                    <TableCell>Phone Number</TableCell>
                                    <TableCell>Registered from Transformation app</TableCell>
                                    <TableCell>ECB</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {paginatedRegisteredMembers.map((member) => (
                                    <TableRow key={member.id}>
                                      <TableCell>{member.name || '-'}</TableCell>
                                      <TableCell>{member.ceaNumber || '-'}</TableCell>
                                      <TableCell>{member.mobile || '-'}</TableCell>
                                      <TableCell>
                                        {member.registeredFromTransformationApp ? 'Yes' : 'No'}
                                      </TableCell>
                                      <TableCell>
                                        {member.ecbCount > 0 ? (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => void handleOpenMemberEcbDialog(member)}
                                            disabled={!member.ceaNumber}
                                          >
                                            View ECBs
                                          </Button>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            No ECBs invited
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {registeredMembers.length > ROWS_PER_PAGE && (
                              <TablePagination
                                component="div"
                                count={registeredMembers.length}
                                page={registeredPage}
                                onPageChange={(_, newPage) => setRegisteredPage(newPage)}
                                rowsPerPage={ROWS_PER_PAGE}
                                rowsPerPageOptions={[ROWS_PER_PAGE]}
                              />
                            )}
                          </Stack>
                        )
                      )}
                      {eventDetailTab === 'ecbs' && (
                        ecbInvitesError ? (
                          <Alert severity="error">{ecbInvitesError}</Alert>
                        ) : ecbInvitesLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : ecbInvites.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No ECBs.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                endIcon={<KeyboardArrowDownIcon />}
                                onClick={(e) => setEcbExportAnchor(e.currentTarget)}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Export
                              </Button>
                              <Menu
                                anchorEl={ecbExportAnchor}
                                open={Boolean(ecbExportAnchor)}
                                onClose={() => setEcbExportAnchor(null)}
                              >
                                <MenuItem onClick={() => { handleExportEcbs('csv'); setEcbExportAnchor(null); }}>
                                  Export as CSV
                                </MenuItem>
                                <MenuItem onClick={() => { handleExportEcbs('excel'); setEcbExportAnchor(null); }}>
                                  Export as Excel
                                </MenuItem>
                              </Menu>
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Agency</TableCell>
                                    <TableCell>Phone Number</TableCell>
                                    <TableCell>Invited By</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {paginatedEcbInvites.map((ecb) => (
                                    <TableRow key={ecb.id}>
                                      <TableCell>{ecb.name || '-'}</TableCell>
                                      <TableCell>{ecb.agency || '-'}</TableCell>
                                      <TableCell>{ecb.mobile || '-'}</TableCell>
                                      <TableCell>
                                        {ecb.invitedByName || ecb.invitedBy || '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {ecbInvites.length > ROWS_PER_PAGE && (
                              <TablePagination
                                component="div"
                                count={ecbInvites.length}
                                page={ecbsPage}
                                onPageChange={(_, newPage) => setEcbsPage(newPage)}
                                rowsPerPage={ROWS_PER_PAGE}
                                rowsPerPageOptions={[ROWS_PER_PAGE]}
                              />
                            )}
                          </Stack>
                        )
                      )}
                    </Box>
                  </Stack>
                );
              })()}
            </Box>
          </Box>
        ) : mainTab === 'events' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 2fr) 3px 3fr' },
              gap: 2,
              minHeight: 480,
            }}
          >
            {/* Left: event list (single-select) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <TextField
                size="small"
                placeholder="Search events..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 520,
                }}
              >
                {filteredEvents.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No upcoming events.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding subheader={<li />}>
                    {groupedEvents.map((group) => (
                      <li key={group.key}>
                        <ul style={{ padding: 0, listStyle: 'none' }}>
                          <ListSubheader
                            disableSticky={false}
                            sx={{
                              bgcolor: 'var(--color-surface)',
                              color: 'var(--color-text-strong)',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              lineHeight: '2.25rem',
                              borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
                            }}
                          >
                            {group.label}
                          </ListSubheader>
                          {group.events.map((event) => {
                            const isSelected = selectedEventIds.has(event.id);

                            return (
                              <ListItemButton
                                key={event.id}
                                selected={isSelected}
                                onClick={() => toggleEvent(event.id)}
                                sx={{
                                  alignItems: 'flex-start',
                                  borderLeft: isSelected
                                    ? '3px solid var(--color-accent)'
                                    : '3px solid transparent',
                                  '&.Mui-selected': {
                                    bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                                    '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.15)' },
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {event.name}
                                      </Typography>
                                      <Chip
                                        label={event.sourceType === 'TRANS' ? 'Ad-hoc' : 'HIP'}
                                        size="small"
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                      />
                                    </Stack>
                                  }
                                  secondary={
                                    <Stack spacing={0.25} component="span">
                                      <Typography variant="caption" color="text.secondary" component="span">
                                        {formatDateRange(event.startDate, event.endDate)}
                                      </Typography>
                                      {event.venue && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          component="span"
                                        >
                                          {event.venue}
                                        </Typography>
                                      )}
                                      {event.assignedCategories.length > 0 && (
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          sx={{ mt: 0.5, flexWrap: 'wrap' }}
                                          component="span"
                                        >
                                          {event.assignedCategories.map((c) => (
                                            <Chip
                                              key={c}
                                              size="small"
                                              label={formatCategoryLabel(c)}
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                          ))}
                                        </Stack>
                                      )}
                                      {event.assignedProjects && event.assignedProjects.length > 0 && (
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          sx={{ mt: 0.5, flexWrap: 'wrap' }}
                                          component="span"
                                        >
                                          {event.assignedProjects.map((project) => (
                                            <Chip
                                              key={project.projectId}
                                              size="small"
                                              variant="outlined"
                                              label={
                                                project.projectName ||
                                                projectNameById.get(project.projectId) ||
                                                project.projectId
                                              }
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                          ))}
                                        </Stack>
                                      )}
                                    </Stack>
                                  }
                                />
                              </ListItemButton>
                            );
                          })}
                        </ul>
                      </li>
                    ))}
                  </List>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {filteredEvents.length} event{filteredEvents.length === 1 ? '' : 's'}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            {/* Right: bulk category assignment */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {selectedEvents.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Select an event on the left to assign categories, projects, trainers, and location settings.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {focusedEvent && (
                    // Categorize-Events single-event header — kept lean.
                    // The Registration QR button + member tabs that used
                    // to live here have moved to the Event Overview tab.
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {focusedEvent.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateRange(focusedEvent.startDate, focusedEvent.endDate)}
                      </Typography>
                      {focusedEvent.venue && (
                        <Typography variant="body2" color="text.secondary">
                          {focusedEvent.venue}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Divider />

                  {focusedEvent?.description && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Description
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                          borderRadius: 1,
                          bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                          color: 'var(--color-text)',
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          maxHeight: 260,
                          overflow: 'auto',
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 1,
                          },
                          '& p': {
                            mt: 0,
                            mb: 1,
                          },
                          '& p:last-child': {
                            mb: 0,
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: normalizeEventDescriptionHtml(focusedEvent.description),
                        }}
                      />
                    </Box>
                  )}

                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={locationRequired}
                          onChange={(_, checked) => {
                            setLocationRequiredDirtied(true);
                            setLocationRequired(checked);
                          }}
                        />
                      }
                      label="Requires attendee location"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                      When unchecked, agents do not need to enable device location to mark attendance.
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      QR refresh interval
                    </Typography>
                    <TextField
                      size="small"
                      value={qrRefreshIntervalInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setQrRefreshIntervalInput(raw);
                        setQrRefreshIntervalDirtied(true);
                        setQrRefreshIntervalSeconds(parseQrRefreshIntervalInput(raw));
                      }}
                      onBlur={() => {
                        setQrRefreshIntervalInput(
                          formatQrRefreshIntervalInput(qrRefreshIntervalSeconds),
                        );
                      }}
                      placeholder="30"
                      helperText="0 = do not refresh the QR. CPD / Generic CPD default to 30."
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">seconds</InputAdornment>
                        ),
                      }}
                      sx={{
                        width: 140,
                        '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' },
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Categories
                    </Typography>
                    {displayedCategories.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No categories available.
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {displayedCategories.map((c) => {
                          const checked = selectedCategories.has(c);
                          const isSpecialCPD =
                            c.toUpperCase() === 'CPD' || c.toUpperCase() === 'GENERIC CPD';
                          const disabled = recruitmentSelected && c !== RECRUITMENT_CATEGORY;
                          return (
                            <Chip
                              key={c}
                              label={formatCategoryLabel(c)}
                              onClick={() => {
                                if (!disabled && !isSpecialCPD) toggleCategory(c);
                              }}
                              color={checked ? 'primary' : 'default'}
                              variant={checked ? 'filled' : 'outlined'}
                              clickable={!disabled && !isSpecialCPD}
                              disabled={disabled}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Projects
                    </Typography>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      size="small"
                      disabled={recruitmentSelected}
                      options={projects}
                      // Convert the Set<string> of selected IDs into the
                      // matching TopViewingSitePlan objects so the Autocomplete
                      // can render Chips with project names instead of IDs.
                      value={Array.from(selectedProjectIds)
                        .map((id) => projects.find((p) => p.projectId === id))
                        .filter((p): p is TopViewingSitePlan => Boolean(p))}
                      onChange={(_, value) => {
                        setProjectsDirtied(true);
                        setSelectedProjectIds(new Set(value.map((p) => p.projectId)));
                      }}
                      getOptionLabel={(opt) => opt.projectName || opt.projectId}
                      isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          autoComplete="off"
                          inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                          placeholder={
                            selectedProjectIds.size === 0 ? 'Select projects' : ''
                          }
                          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((opt, idx) => (
                          <Chip
                            {...getTagProps({ index: idx })}
                            key={opt.projectId}
                            label={opt.projectName || opt.projectId}
                            size="small"
                          />
                        ))
                      }
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Trainers
                    </Typography>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      size="small"
                      disabled={recruitmentSelected}
                      options={trainerSearchOptions}
                      filterOptions={(x) => x}
                      loading={trainerSearchLoading}
                      value={selectedTrainers}
                      inputValue={trainerSearchInput}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'reset') {
                          setTrainerSearchInput('');
                          return;
                        }
                        if (reason === 'input' || reason === 'clear') {
                          setTrainerSearchInput(value);
                        }
                      }}
                      onChange={(_, value) => {
                        setTrainersDirtied(true);
                        setSelectedTrainers(value);
                        setTrainerSearchInput('');
                      }}
                      getOptionLabel={(opt) =>
                        opt.name ? `${opt.name} (${opt.ceaNumber})` : opt.ceaNumber
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.ceaNumber.toUpperCase() === value.ceaNumber.toUpperCase()
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          autoComplete="off"
                          inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                          placeholder={
                            selectedTrainers.length === 0
                              ? 'Search CEANumber (min 3 characters)'
                              : ''
                          }
                          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((opt, idx) => (
                          <Chip
                            {...getTagProps({ index: idx })}
                            key={opt.ceaNumber}
                            label={
                              opt.name ? `${opt.name} (${opt.ceaNumber})` : opt.ceaNumber
                            }
                            size="small"
                          />
                        ))
                      }
                      noOptionsText={
                        trainerSearchInput.trim().length <= 2
                          ? 'Type at least 3 characters of a CEANumber'
                          : trainerSearchLoading
                            ? 'Searching…'
                            : 'No matching agents'
                      }
                    />
                  </Box>

                  {/* Member tabs (Registered Users / Attended Users / ECBs)
                      and the Registration QR live on the Event Overview
                      tab, not here. The Categorize Events tab focuses on
                      categories, projects, trainers, and location-required.
                      Ad-hoc events are managed in Manage Events (Ad-hoc). */}

                </Stack>
              )}
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose} sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }} disabled={saving}>
          Close
        </Button>
        {mainTab === 'events' && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={selectedEvents.length === 0 || !isDirty || saving}
            sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
          >
            {saving ? 'Saving...' : 'Save Categories'}
          </Button>
        )}
      </DialogActions>
      <Dialog
        open={Boolean(reviewDetail)}
        onClose={() => setReviewDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Training Review
          {reviewDetail?.name ? ` — ${reviewDetail.name}` : ''}
          {reviewDetail?.ceaNumber ? ` (${reviewDetail.ceaNumber})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {reviewDetail && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Review
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', color: 'var(--color-text-strong)' }}
                >
                  {reviewDetail.review || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Learning Points
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', color: 'var(--color-text-strong)' }}
                >
                  {reviewDetail.learningPoints || '—'}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={memberEcbDialogOpen}
        onClose={() => setMemberEcbDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{memberEcbDialogTitle || 'Invited ECBs'}</DialogTitle>
        <DialogContent>
          {memberEcbInvitesError ? (
            <Alert severity="error">{memberEcbInvitesError}</Alert>
          ) : memberEcbInvitesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : memberEcbInvites.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No ECBs invited.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Agency</TableCell>
                    <TableCell>Phone Number</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {memberEcbInvites.map((ecb) => (
                    <TableRow key={ecb.id}>
                      <TableCell>{ecb.name || '-'}</TableCell>
                      <TableCell>{ecb.agency || '-'}</TableCell>
                      <TableCell>{ecb.mobile || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberEcbDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={tagRemovalWarningEventNames.length > 0}
        onClose={() => {
          if (!saving) setTagRemovalWarningEventNames([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Registered users may lose visibility</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Removing an existing category or project tag from the following event
            {tagRemovalWarningEventNames.length === 1 ? '' : 's'} may mean registered users need
            to show all trainings on their calendar in the transformation app to view the training,
            or may not be able to view it at all.
          </Alert>
          <Stack spacing={0.75}>
            {tagRemovalWarningEventNames.map((eventName, index) => (
              <Box
                key={eventName}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  p: 1,
                  border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                  borderRadius: 1,
                  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 24 }}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2">{eventName}</Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagRemovalWarningEventNames([])} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={async () => {
              setTagRemovalWarningEventNames([]);
              await saveCategories();
            }}
          >
            Continue Saving
          </Button>
        </DialogActions>
      </Dialog>
      <EventAttendanceQrDialog
        open={Boolean(sessionQrTarget)}
        onClose={() => setSessionQrTarget(null)}
        eventId={sessionQrTarget?.session.eventId || ''}
        eventName={sessionQrTarget?.eventName || ''}
        sessionId={sessionQrTarget?.session.id ?? null}
        sessionName={sessionQrTarget?.session.name ?? null}
        mode={sessionQrTarget?.mode || 'clockIn'}
        locationRequired={overviewFocusedEvent?.locationRequired === true}
      />
      <SessionAttendeesDialog
        open={Boolean(sessionAttendeesTarget)}
        onClose={() => setSessionAttendeesTarget(null)}
        sessionId={sessionAttendeesTarget?.id ?? null}
        sessionName={sessionAttendeesTarget?.name ?? ''}
        sessionDate={sessionAttendeesTarget?.sessionDate ?? ''}
        eventId={sessionAttendeesTarget?.eventId ?? ''}
        eventName={overviewFocusedEvent?.name ?? ''}
        isAdmin
        locationRequired={overviewFocusedEvent?.locationRequired === true}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            sx={{ width: '100%' }}
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Dialog>
  );
};


export default ManageEventsDialog;
